/**
 * Look control — where the player is facing.
 *
 * Kate described Minecraft controls: "Minecraft controls up up down side to side
 * forward backwards", and separately "you look and you go forward to move". So looking
 * is its own axis, independent of the movement keys, and drives both the camera and
 * which way forward is.
 *
 * Desktop uses pointer lock; touch drag is handled by the touch controls and fed in
 * through the same `applyDelta`.
 */
import * as THREE from 'three'

/** Radians of rotation per pixel of pointer movement at sensitivity 1. */
const RADIANS_PER_PIXEL = 0.0022

/** Kate's birds fly only to tree height, so there's no reason to look straight up. */
const PITCH_LIMIT = Math.PI / 2 - 0.08

export class LookControl {
  yaw = 0
  pitch = 0
  sensitivity = 1
  /** True while the pointer is locked to the canvas. */
  locked = false

  private readonly canvas: HTMLCanvasElement

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
  }

  attach(): void {
    this.canvas.addEventListener('click', this.requestLock)
    document.addEventListener('pointerlockchange', this.onLockChange)
    document.addEventListener('mousemove', this.onMouseMove)
  }

  detach(): void {
    this.canvas.removeEventListener('click', this.requestLock)
    document.removeEventListener('pointerlockchange', this.onLockChange)
    document.removeEventListener('mousemove', this.onMouseMove)
  }

  private readonly requestLock = (): void => {
    // Requesting a lock that's already held throws in some browsers.
    if (document.pointerLockElement !== this.canvas) void this.canvas.requestPointerLock()
  }

  private readonly onLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas
  }

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) return
    this.applyDelta(event.movementX, event.movementY)
  }

  /**
   * Rotate by a pointer delta in pixels. Shared by mouse look and touch drag.
   *
   * `pitch` is the camera's elevation offset, NOT a view angle: a positive pitch lifts
   * the camera above the player, which shows you the ground — i.e. looking *down*. So
   * moving the pointer up (a negative dy) has to DECREASE pitch to look up. Getting
   * this backwards inverted the vertical axis, which is exactly how it played.
   */
  applyDelta(dx: number, dy: number): void {
    const scale = RADIANS_PER_PIXEL * this.sensitivity
    this.yaw -= dx * scale
    this.pitch = THREE.MathUtils.clamp(this.pitch + dy * scale, -PITCH_LIMIT, PITCH_LIMIT)
  }
}
